import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '@/firebase/config';

type NumberedPrefix = 'CUST' | 'PROD' | 'QT' | 'PO' | 'IN' | 'DLV' | 'PTNR';

// コードは常に "PREFIX-00001"（日付なし5桁ゼロ埋め）で採番する。
// 案件に紐づく帳票は code に「受注番号-枝番」(0001-01) を入れるため、この採番は
// 商品マスタ(PROD)や、案件に紐づかない単独帳票のフォールバック採番でのみ使用する。
function formatCode(prefix: NumberedPrefix, current: number): string {
  return `${prefix}-${String(current).padStart(5, '0')}`;
}

export async function generateCode(prefix: NumberedPrefix): Promise<string> {
  const counterRef = doc(db, 'counters', prefix);

  const code = await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const current = snap.exists() ? (snap.data()['current'] as number) : 0;
    const next = current + 1;
    tx.set(counterRef, { current: next, updatedAt: serverTimestamp() }, { merge: true });
    return formatCode(prefix, next);
  });

  return code;
}

/**
 * 注文書の取引先IDを自動採番する。counters/PTNR で連番を管理し、"PTNR-00001" 形式で返す。
 * 取引先IDは手動入力しない方針のため、継承元の取引先IDが無い場合のフォールバック採番に使う。
 * 顧客マスタの取引先コード採番（CUST_SEQ）とはカウンタを分離し、番号の取り合いを防ぐ。
 */
export async function generatePartnerCode(): Promise<string> {
  return generateCode('PTNR');
}

/**
 * 合算請求書コードを採番する。counters/COMBINED で連番を管理し、"A0001" 形式で返す。
 * 合算請求は複数案件を横断するため「受注番号-枝番」を持てず、独立した通し番号を割り当てる。
 * 9999を超えたら桁あふれして "A10000" 以上で表示する。
 */
export async function generateCombinedCode(): Promise<string> {
  const counterRef = doc(db, 'counters', 'COMBINED');

  const code = await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const current = snap.exists() ? (snap.data()['current'] as number) : 0;
    const next = current + 1;
    tx.set(counterRef, { current: next, updatedAt: serverTimestamp() }, { merge: true });
    return `A${String(next).padStart(4, '0')}`;
  });

  return code;
}

/**
 * 顧客（取引先）コードを採番する。
 * counters/CUST_SEQ で連番を管理し、数字のみ4桁ゼロパディングで返す。
 * 9999を超えたら桁あふれして5桁以上で表示する。
 * 例: 1 → "0001", 9999 → "9999", 10000 → "10000"
 */
export async function generateCustomerCode(): Promise<string> {
  const counterRef = doc(db, 'counters', 'CUST_SEQ');

  const code = await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const current = snap.exists() ? (snap.data()['current'] as number) : 0;
    const next = current + 1;
    tx.set(counterRef, { current: next, updatedAt: serverTimestamp() }, { merge: true });
    return next <= 9999 ? String(next).padStart(4, '0') : String(next);
  });

  return code;
}

/**
 * 受注番号（案件番号）を採番する。
 * カウンタ方式ではなく「未削除（isDeleted=false）の受注のうち最大の案件番号 + 1」を採番し、
 * 4桁ゼロパディング（9999超で桁あふれ）で返す。論理削除済みは対象外。
 * - 末尾（最大番号）の案件を削除した場合は、その番号を詰めて再利用する。
 *   例: 0001,0002,0003 のうち 0003 を削除 → 次は 0003。
 * - 中間の案件を削除した場合は欠番のまま最大 + 1 を採番する（番号は詰めない）。
 *   例: 0001,0002,0003 のうち 0002 を削除 → 次は 0004（0002 は欠番）。
 * 例: （なし）→ "0001", 9998まで → "9999", 9999まで → "10000"
 */
export async function generateOrderNo(): Promise<string> {
  const snap = await getDocs(
    query(
      collection(db, 'orders'),
      where('isDeleted', '==', false),
      orderBy('orderNo', 'desc'),
      limit(1),
    )
  );
  const maxNo = snap.docs.length === 0
    ? 0
    : parseInt((snap.docs[0].data()['orderNo'] as string | undefined) ?? '0', 10);
  const next = Number.isNaN(maxNo) ? 1 : maxNo + 1;
  return next <= 9999 ? String(next).padStart(4, '0') : String(next);
}

/**
 * 枝番付き受注番号を生成する純粋関数（Firestoreアクセスなし）。
 * 枝番は2桁ゼロ埋め。例: generateBranchNumber('0001', 1) → "0001-01"、99超で桁あふれ。
 */
export function generateBranchNumber(parentNo: string, nextBranch: number): string {
  return `${parentNo}-${String(nextBranch).padStart(2, '0')}`;
}

/**
 * 受注ドキュメントIDから、指定コレクション内で「案件×文書種別」ごとの次の枝番「受注番号-連番」を採番する。
 * - 受注番号は orders/{orderId} の orderNo（無ければ code）を親番号に使う。
 * - 既存ドキュメント（同 orderId）の code 末尾の最大 + 1。1本目は 受注番号-01。
 * - 論理削除済み（isDeleted=true）は採番対象から除外する。末尾を削除して作り直した場合に
 *   同じ枝番を再利用でき、「間違えて作成→削除→作り直し」で飛び番が生じない。
 * - orderId 無し / 受注が見つからない場合は null。
 */
export async function nextDocumentBranchNumber(
  collectionName: string,
  orderId: string | null | undefined,
): Promise<string | null> {
  if (!orderId) return null;
  const orderSnap = await getDoc(doc(db, 'orders', orderId));
  if (!orderSnap.exists()) return null;
  const od = orderSnap.data();
  const orderNo = (od['orderNo'] as string) ?? (od['code'] as string) ?? null;
  if (!orderNo) return null;
  const snap = await getDocs(query(collection(db, collectionName), where('orderId', '==', orderId)));
  let maxSeq = 0;
  snap.docs.forEach((d) => {
    const data = d.data();
    // 論理削除済みは採番対象外（末尾削除時に番号を再利用するため）
    if (data['isDeleted'] === true) return;
    // 案件帳票の code は「受注番号-枝番」(0001-01)。末尾の枝番を読み取る。
    const c = data['code'] as string | undefined;
    if (!c) return;
    const dash = c.lastIndexOf('-');
    if (dash < 0) return;
    const seq = parseInt(c.slice(dash + 1), 10);
    if (!Number.isNaN(seq) && seq > maxSeq) maxSeq = seq;
  });
  return generateBranchNumber(orderNo, maxSeq + 1);
}
